import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  useAdminPodcasts,
  useCreatePodcast,
  useGenerateAllPodcastContent,
  useGeneratePodcastContent,
  useGeneratePodcastCover,
} from '@/hooks/use-admin'
import type { AdminPodcast } from '@/services/admin.service'
import {
  CheckCircle,
  Clock,
  Headphones,
  Image,
  Loader2,
  Mic,
  Play,
  Plus,
  Volume2,
  Zap,
} from 'lucide-react'
import { useState } from 'react'

const CATEGORY_LABELS: Record<string, string> = {
  career_development: 'Career Development',
  leadership: 'Leadership',
  communication: 'Communication',
  productivity: 'Productivity',
  technical: 'Technical',
  entrepreneurship: 'Entrepreneurship',
  personal_growth: 'Personal Growth',
  job_search: 'Job Search',
  networking: 'Networking',
  finance: 'Finance',
}

const CATEGORY_OPTIONS = Object.entries(CATEGORY_LABELS).map(([value, label]) => ({
  value,
  label,
}))

function StatusBadge({ status }: { status: string | null }) {
  if (!status) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
        Not generated
      </span>
    )
  }

  switch (status) {
    case 'pending':
    case 'generating':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
          <Clock className="h-3 w-3" />
          {status === 'pending' ? 'Pending' : 'Generating'}
        </span>
      )
    case 'completed':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
          <CheckCircle className="h-3 w-3" />
          Completed
        </span>
      )
    case 'failed':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">
          Failed
        </span>
      )
    default:
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
          {status}
        </span>
      )
  }
}

function AudioBadge({ ready }: { ready: boolean }) {
  if (ready) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
        <Volume2 className="h-3 w-3" />
        Ready
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
      No audio
    </span>
  )
}

function CoverBadge({
  hasCover,
  coverUrl,
  title,
}: { hasCover: boolean; coverUrl: string | null; title: string }) {
  const [showPreview, setShowPreview] = useState(false)

  if (hasCover) {
    return (
      <>
        <button type="button" onClick={() => setShowPreview(true)}>
          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700 hover:bg-green-200 transition-colors cursor-pointer">
            <Image className="h-3 w-3" />
            Has cover
          </span>
        </button>
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription>Cover art preview</DialogDescription>
            </DialogHeader>
            <div className="flex items-center justify-center p-2">
              {coverUrl ? (
                <img
                  src={coverUrl}
                  alt={`Cover of ${title}`}
                  className="max-w-full max-h-[400px] rounded-lg object-contain"
                />
              ) : (
                <div className="h-48 w-48 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <Headphones className="h-16 w-16 text-white/80" />
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
      No cover
    </span>
  )
}

function PodcastRow({
  podcast,
  onGenerate,
  onGenerateCover,
  isGenerating,
  isGeneratingCover,
}: {
  podcast: AdminPodcast
  onGenerate: (id: number) => void
  onGenerateCover: (id: number) => void
  isGenerating: boolean
  isGeneratingCover: boolean
}) {
  const canGenerate =
    !podcast.generation_status ||
    podcast.generation_status === 'failed' ||
    podcast.generation_status === 'pending' ||
    podcast.generation_status === 'generating' ||
    (podcast.generation_status === 'completed' && !podcast.audio_ready)

  const buttonLabel = !podcast.generation_status
    ? 'Generate'
    : podcast.generation_status === 'completed' && !podcast.audio_ready
      ? 'Retry Audio'
      : podcast.generation_status !== 'completed'
        ? 'Retry'
        : 'Generate'

  return (
    <tr className="border-b last:border-b-0 hover:bg-muted/50">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
            <Headphones className="h-5 w-5 text-white/80" />
          </div>
          <div>
            <div className="font-medium text-sm">{podcast.title}</div>
            <div className="text-xs text-muted-foreground line-clamp-1 max-w-xs">
              {podcast.description}
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-muted-foreground">
        {CATEGORY_LABELS[podcast.category] || podcast.category}
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={podcast.generation_status} />
      </td>
      <td className="px-4 py-3">
        <AudioBadge ready={podcast.audio_ready} />
      </td>
      <td className="px-4 py-3">
        <CoverBadge
          hasCover={podcast.has_cover}
          coverUrl={podcast.cover_url}
          title={podcast.title}
        />
      </td>
      <td className="px-4 py-3">
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onGenerate(podcast.id)}
            disabled={!canGenerate || isGenerating}
          >
            {isGenerating ? (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            ) : (
              <Play className="h-3 w-3 mr-1" />
            )}
            {buttonLabel}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onGenerateCover(podcast.id)}
            disabled={isGeneratingCover}
          >
            {isGeneratingCover ? (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            ) : (
              <Image className="h-3 w-3 mr-1" />
            )}
            Cover
          </Button>
        </div>
      </td>
    </tr>
  )
}

export function AdminPodcastsPage() {
  const { data, isLoading, refetch } = useAdminPodcasts()
  const generatePodcast = useGeneratePodcastContent()
  const generateAll = useGenerateAllPodcastContent()
  const generateCover = useGeneratePodcastCover()
  const createPodcast = useCreatePodcast()
  const [showGenerateAllDialog, setShowGenerateAllDialog] = useState(false)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [generatingPodcastId, setGeneratingPodcastId] = useState<number | null>(null)
  const [generatingCoverId, setGeneratingCoverId] = useState<number | null>(null)
  const [lastResult, setLastResult] = useState<string | null>(null)

  // Create form state
  const [newTitle, setNewTitle] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [newCategory, setNewCategory] = useState('career_development')

  const handleGenerate = async (podcastId: number) => {
    setGeneratingPodcastId(podcastId)
    try {
      const result = await generatePodcast.mutateAsync(podcastId)
      setLastResult(`Queued: ${result.message}`)
    } catch (err) {
      setLastResult(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setGeneratingPodcastId(null)
    }
  }

  const handleGenerateCover = async (podcastId: number) => {
    setGeneratingCoverId(podcastId)
    try {
      await generateCover.mutateAsync(podcastId)
      setLastResult('Cover art generated')
    } catch (err) {
      setLastResult(`Cover error: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setGeneratingCoverId(null)
    }
  }

  const handleGenerateAll = async () => {
    try {
      const result = await generateAll.mutateAsync()
      setLastResult(result.message)
      setShowGenerateAllDialog(false)
    } catch (err) {
      setLastResult(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`)
      setShowGenerateAllDialog(false)
    }
  }

  const handleCreate = async () => {
    if (!newTitle.trim() || !newDescription.trim()) return
    try {
      await createPodcast.mutateAsync({
        title: newTitle.trim(),
        description: newDescription.trim(),
        category: newCategory,
      })
      setLastResult('Podcast created')
      setShowCreateDialog(false)
      setNewTitle('')
      setNewDescription('')
      setNewCategory('career_development')
    } catch (err) {
      setLastResult(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
  }

  const podcasts = data?.podcasts || []
  const generated = podcasts.filter((p) => p.generation_status === 'completed').length
  const pending = podcasts.filter(
    (p) => p.generation_status === 'pending' || p.generation_status === 'generating',
  ).length
  const notGenerated = podcasts.filter((p) => !p.generation_status).length

  return (
    <div className="p-8 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Podcast Content</h1>
          <p className="text-muted-foreground">
            Manage AI-generated podcast episodes with cover art
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()} disabled={isLoading}>
            Refresh
          </Button>
          <Button variant="outline" onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Create Podcast
          </Button>
          <Button onClick={() => setShowGenerateAllDialog(true)} disabled={generateAll.isPending}>
            <Zap className="h-4 w-4 mr-1" />
            Generate All
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Podcasts</CardTitle>
            <Mic className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{podcasts.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Generated</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{generated}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Progress</CardTitle>
            <Clock className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Not Generated</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-500">{notGenerated}</div>
          </CardContent>
        </Card>
      </div>

      {/* Status message */}
      {lastResult && (
        <div className="rounded-md bg-blue-50 px-4 py-3 text-sm text-blue-700">
          {lastResult}
          <button
            type="button"
            className="ml-2 text-blue-500 hover:text-blue-800"
            onClick={() => setLastResult(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Podcast table */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      ) : podcasts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Headphones className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No podcasts yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create your first podcast to get started
            </p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Create Podcast
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                    Podcast
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                    Content
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                    Audio
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                    Cover
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {podcasts.map((podcast) => (
                  <PodcastRow
                    key={podcast.id}
                    podcast={podcast}
                    onGenerate={handleGenerate}
                    onGenerateCover={handleGenerateCover}
                    isGenerating={generatingPodcastId === podcast.id}
                    isGeneratingCover={generatingCoverId === podcast.id}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Generate All Confirmation */}
      <Dialog open={showGenerateAllDialog} onOpenChange={setShowGenerateAllDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate All Podcast Content</DialogTitle>
            <DialogDescription>
              This will queue content generation for {notGenerated} podcasts that haven't been
              generated yet. Each podcast will get 3-5 episode scripts generated by AI, followed by
              automatic audio generation. Podcasts that already have content will be skipped.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateAllDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleGenerateAll} disabled={generateAll.isPending}>
              {generateAll.isPending ? 'Queuing...' : `Generate ${notGenerated} Podcasts`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Podcast Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Podcast</DialogTitle>
            <DialogDescription>
              Add a new podcast to the catalog. You can generate episodes and cover art after
              creation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                placeholder="Career Growth Conversations"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="A podcast about navigating career transitions and professional growth..."
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select value={newCategory} onValueChange={setNewCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={createPodcast.isPending || !newTitle.trim() || !newDescription.trim()}
            >
              {createPodcast.isPending ? 'Creating...' : 'Create Podcast'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
